import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    TableContainer,
    Table,
    Paper,
    Container,
    Divider
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { CustomTableProps } from '@propTypes/index';
import { getComparator, stableSortNonQuery } from './helpers'
import { SimpleSearch, CustomTableHead, CustomTableBody, CustomTableFooter, TableTools } from './components'
import { CustomTableStyle } from 'customStyles/CustomTableStyle';
import { useTableStore } from 'state/TableStore';
import { PageNames } from 'interfaces/config';
import { MenuState } from 'customEnums/MenuState';
import { extractPageName } from './helpers';
import { AdvancedSearchAction } from "customEnums/TransactionSummaryEnums";
import FilterListIcon from '@mui/icons-material/FilterList';
import { useUserStore } from 'state';

export const CustomTable: React.FC<CustomTableProps> = ({
    tableConfig,
    rows,
    hideSearch,
    openModalBlur,
    openAgencyEdit,
    tcns,
    totalRecords,
    setSearched,
    handleDeselectAll,
    setValue,
    handleSelectAll,
    setDisplaySavedMessage,
    setCreateSearch,
    setSearchLength,
    advancedSearchNumParams,
    isSelectedRow,
    currentPage,
    pageSize,
    loading,
    columnStateUpdate,
    handleHideAll,
    handleShowAll,
    handleReset,
    searched,
    selected,
    setSelected,
    value,
    selectAllState,
    rowClickHandler,
    tableName,
    setOrderBy,
    dispatchFormValues,
    isCurrentPageAllSelected,
    isCurrentPagePartiallySelected
}) => {
    const locationObj = useLocation()
    const pageName = extractPageName(locationObj.pathname);

    const applicantDetails = PageNames.APPLICANT_DETAILS === pageName

    const [menuState, setMenuState] = useState(MenuState.CATEGORY)
    const setResetTable = useTableStore(state => state.setResetTable)
    const pagination = useTableStore(state => state.pagination)
    const setZoomLevel = useTableStore(state => state.setZoomLevel)
    const resetTableState = useTableStore(state => state.resetTableState)
    const setAppliedFiltersByTable = useTableStore(state => state.setAppliedFiltersByTable)
    const setFiltersAppliedByTable = useTableStore(state => state.setFiltersAppliedByTable)
    const selectedIds = useTableStore(state => state.selected)
    const headerButtonConfig = useTableStore(state => state.headerButtonConfig)
    const setHeaderButtonConfig = useTableStore(state => state.setHeaderButtonConfig)
    const filtersAppliedByTable = useTableStore(state => state.filtersAppliedByTable)
    const appliedFiltersByTable = useTableStore(state => state.appliedFiltersByTable);
    const filtersApplied = Boolean(filtersAppliedByTable?.[tableName]);
    const appliedFilters = appliedFiltersByTable?.[tableName] ?? [];
    const [datePlaceHolder, setDatePlaceholder] = React.useState(false)
    const [hoverLabel, setHoverLabel] = React.useState("")
    const [visibleRows, setVisibleRows] = useState([])
    const config = useUserStore(state => state.config)
    const { hardLimit } = config.transactionViewing
    const tableRef = useRef<any>(null);


    const tableBodyRef = useRef()

    const differentBgColor = [
        "rb-summary-audit-history",
        "rb-sub-audit-history",
        "reject-codes",
        "rapback-maintenance"
    ];

    const tableBgColor = differentBgColor.includes(tableName) ? "background.default" : 'common.black'

    const tablesToPad = [
        "transaction-summary",
        "rapback-maintenance",
        "rapback-validations",
        "rapback-prenotifications",
        "rapback-summary",
        "agency-management",
        "user-management",
        "audit-history",
    ];

    const { tableHeaderCells, useQueryPagination } = tableConfig[tableName]

    const { orderBy, order } = pagination[tableName]

    const adjustZoom = useCallback(() => {
        const zoom = ((window.outerWidth - 10) / window.innerWidth)

        if (zoom >= 1) {
            setZoomLevel(zoom);
        }
    }, [setZoomLevel])


    const resetTable = useCallback(() => {
        console.log('debug resetTable callback setValue: ', setValue)
        //Passed In
        dispatchFormValues?.({ type: AdvancedSearchAction.RESET, hardLimit })
        setSelected?.([])
        handleDeselectAll?.()
        setSearched?.(false)
        setSearchLength?.(0)
        setValue?.([])
        setHeaderButtonConfig({ ...headerButtonConfig, disabled: true })

        //Local State
        setMenuState(MenuState.CATEGORY)
        setDatePlaceholder(false)

        //TableStore
        resetTableState()
        if (tableName) {
            setAppliedFiltersByTable(tableName, [])
            setFiltersAppliedByTable(tableName, false)
        }
    }, [
        dispatchFormValues,
        handleDeselectAll,
        hardLimit,
        headerButtonConfig,
        setAppliedFiltersByTable,
        setFiltersAppliedByTable,
        setHeaderButtonConfig,
        setMenuState,
        setDatePlaceholder,
        setSearchLength,
        setSelected,
        setSearched,
        setValue,
        tableName,
        resetTableState
    ])

    useEffect(() => {
        adjustZoom();
        setResetTable(resetTable)
        window.addEventListener("resize", adjustZoom);
        return () => {
            window.removeEventListener("resize", adjustZoom);
        }
    }, [adjustZoom, resetTable, setResetTable]);

    useEffect(() => {
        if (tableRef.current && pageName !== "applicant-details") {
            tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
        }
    }, [pageName])

    const filteredRows = useMemo(() => {
        if (!rows) return [];

        if (!filtersApplied) return rows;

        if (!appliedFilters || appliedFilters.length === 0) return [];

        return rows.filter((row: any) =>
            appliedFilters.every((filter: any) => {
                const rowValue = row?.[filter.filterColumn];
                return (filter.activeValues ?? []).some((sv: any) =>
                    sv === rowValue || String(sv) === String(rowValue)
                );
            })
        );
    }, [rows, filtersApplied, appliedFilters]);

    //update row display for userManagement here
    useEffect(() => {
        if (!filteredRows) return
        console.log('debug user management data CustomTable resort check: ', { rows: filteredRows, useQueryPagination, tableName, order, orderBy })

        if (orderBy === 'checkbox') {
            const idSet = new Set(selectedIds);

            const withIndex = filteredRows.map((row: any, index: number) => ({ row, index }));

            withIndex.sort((a, b) => {
                const aSelected = idSet.has(a.row.transactionNumber) ? 1 : 0;
                const bSelected = idSet.has(b.row.transactionNumber) ? 1 : 0;

                if (aSelected !== bSelected) return order === 'asc' ? (bSelected - aSelected) : (aSelected - bSelected);

                return a.index - b.index;
            });

            const sortedRows = withIndex.map(x => x.row);
            setVisibleRows(sortedRows)
            return;
        }

        if ((useQueryPagination) || (!order && !orderBy)) {
            if ((currentPage && pageSize)) {
                setVisibleRows(filteredRows)
            }
        }

        if (!useQueryPagination && (order && orderBy)) {
            const visibleRows = stableSortNonQuery(filteredRows, getComparator(order, orderBy, pageName, tableName), orderBy).slice(

                currentPage * pageSize,
                currentPage * pageSize + pageSize,
            )
            setVisibleRows(visibleRows);
        }
    }, [
        filteredRows,
        filteredRows?.length,
        selectedIds,
        order,
        orderBy,
        pageSize,
        currentPage,
        useQueryPagination,
        tableName,
        pageName
    ])


    useEffect(() => {
        if (isSelectedRow && isSelectedRow.length > 0) {
            setSelected(isSelectedRow)
        }
    }, [isSelectedRow, setSelected])
    console.log('visible rows CustomTable tableheaderCells ', { tableHeaderCells })

    return (
        <Paper data-testid={`${tableName}-container`} sx={{
            maxHeight: "calc(100% - 40px)",
            display: "flex",
            flexDirection: "column",
            width: '100%',
            backgroundColor: (tableName && tableName !== "rapback-maintenance") ? 'common.black' : 'background.default',
        }}
            elevation={0}>
            {
                tableName &&
                <>
                    {!hideSearch &&
                        <Container disableGutters maxWidth={false} sx={CustomTableStyle.tableTopStyle} data-testid="custom-table-header-container">
                            <SimpleSearch
                                tableConfig={tableConfig}
                                useQueryPagination={useQueryPagination}
                                tableName={tableName}
                                menuState={menuState}
                                setMenuState={setMenuState}
                                searched={searched}
                                value={value}
                                setValue={setValue}
                                label={pageName}
                                advancedSearchNumParams={advancedSearchNumParams ? advancedSearchNumParams : 0}
                                setCreateSearch={setCreateSearch ? setCreateSearch : () => { }}
                                datePlaceHolder={datePlaceHolder}
                                setDatePlaceholder={setDatePlaceholder}
                                setDisplaySavedMessage={setDisplaySavedMessage ? setDisplaySavedMessage : () => { }}
                            />
                            {
                                config && (<TableTools tableName={tableName} resetTable={resetTable} />)
                            }
                        </Container>
                    }
                    {
                        !applicantDetails && (
                            <Divider aria-hidden role="separator" />
                        )
                    }
                    <TableContainer sx={{ boxShadow: "none", borderRadius: 0 }} component={Paper} data-testid={`${tableName}-table-container`}>
                        <Table ref={tableRef} sx={{
                            overFlowX: 'scroll',
                            width: "100%",
                            paddingLeft: tableName && !tablesToPad.includes(tableName) ? 0 : 3,
                            paddingBottom: 1,
                            paddingRight: tableName && !tablesToPad.includes(tableName) ? 0 : 3,
                            backgroundColor: tableBgColor, borderRadius: 0
                        }} stickyHeader aria-label={pageName}>
                            <CustomTableHead
                                tableConfig={tableConfig}
                                tableSortLabelIcon={<FilterListIcon onClick={(e) => { e.stopPropagation() }} />}
                                currentPage={currentPage}
                                selectAllState={selectAllState}
                                tableName={tableName}
                                hoverLabel={hoverLabel}
                                setHoverLabel={setHoverLabel}
                                columnStateUpdate={columnStateUpdate}
                                handleHideAll={handleHideAll}
                                handleShowAll={handleShowAll}
                                handleReset={handleReset}
                                label={pageName}
                                numSelected={selected?.length}
                                rowCount={visibleRows?.length}
                                order={order}
                                orderBy={orderBy}
                                handleInternalSelectAll={handleSelectAll}
                                //onRequestSort={handleRequestSort}
                                visibleRows={visibleRows?.length ? visibleRows : []}
                                tableBgColor={tableBgColor}
                                isCurrentPageAllSelected={isCurrentPageAllSelected}
                                isCurrentPagePartiallySelected={isCurrentPagePartiallySelected}
                            />
                            <CustomTableBody
                                tableBodyRef={tableBodyRef}
                                tableConfig={tableConfig}
                                tableName={tableName}
                                rowClickHandler={rowClickHandler}
                                openModalBlur={openModalBlur}
                                openAgencyEdit={openAgencyEdit}
                                loading={loading}
                                visibleRows={visibleRows?.length ? visibleRows : []}
                                tableBgColor={tableBgColor}
                            />
                        </Table>
                    </TableContainer>
                    <Divider aria-hidden role="separator" />
                    <CustomTableFooter tableBodyRef={tableBodyRef} useQueryPagination={useQueryPagination} tcns={tcns} totalRecords={totalRecords} tableName={tableName} />
                </>
            }
        </Paper>
    )
}
